import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiKeyApi } from '@/api';
import { QUERY_KEY_CONST, QUERY_META_CONST } from '@/constant';
import type { APIKey, APIKeyApi } from '@/type';

export const useAPIKeyCountQuery = (param: APIKeyApi.GetCount.Request) => {
  return useQuery({
    queryFn: () => apiKeyApi.getCount(param).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getCount(param),
  });
};

export const useAPIKeyCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: APIKeyApi.Create.Request) => apiKeyApi.create(param),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getCount(),
      });
    },
    meta: {
      [QUERY_META_CONST.META_KEY.OVERRIDE_GLOBAL_ERROR_HANDLER]: true,
    },
  });
};

export const useAPIKeyDecryptKeyMutation = () => {
  return useMutation({
    mutationFn: (param: APIKeyApi.DecryptAPIKey.Request) =>
      apiKeyApi.decryptAPIKey(param).then(res => res.data.resultData),
  });
};

export const useAPIKeyGenerateKeyMutation = () => {
  return useMutation({
    mutationFn: () => apiKeyApi.generateAPIKey({}),
  });
};

export const useAPIKeyListQuery = () => {
  return useQuery({
    queryFn: () => apiKeyApi.getList({}).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getList(),
  });
};

export const useAPIKeyQuery = (id: APIKey['id']) => {
  return useQuery({
    queryFn: () => apiKeyApi.get({ id }).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.API_KEY_KEYS.get(id),
  });
};

export const useAPIKeyRemoveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: APIKey['id']) => apiKeyApi.remove({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getCount(),
      });
    },
  });
};

export const useAPIKeyUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: APIKeyApi.Update.Request) => apiKeyApi.update(param),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.API_KEY_KEYS.getCount(),
      });
    },
  });
};
